<template>
  <ui-route-aware-sidebar-container
    :id="HIRE_TEAM_DETAILS_MODAL_ID"
    :fallback-route="parentRoute"
    :size="UI.SIZE.SMALL"
    placement="end"
    body-class="px-0"
    fit-workspace
  >
    <template #title>
      <ui-cloak
        :is-visible="fetchState.status.value === 'success'"
        :idle-phase-duration="300"
        :sustain-phase-duration="300"
      >
        {{ details.label }}
      </ui-cloak>
    </template>
    <template #default>
      <ui-cloak
        :is-visible="fetchState.status.value === 'success'"
        :idle-phase-duration="300"
        :sustain-phase-duration="300"
      >
        <template #cloak>
          <div class="page-team-details__preloading">
            <ui-spinner
              :size="UI.SIZE.LARGE"
            />
          </div>
        </template>

        <template #default>
          <ui-wad
            class="animate__animated animate__fadeIn ease-in-out a1"
          >
            <div class="page-team-details__main">

              <!-- Details -->
              <ui-reset ref="reset_detailsForm">
                <details-form
                  v-bind="{
                    callback: updateCallback,
                    isPending: onDetailsPending,
                  }"
                  :default-data="details"
                  @cancel="() => ref_reset_detailsForm?.reset()"
                />
              </ui-reset>

              <hr class="page-team-details__separator">

              <!-- Members -->
              <b-card
                :class="[
                  'page-team-details__members',
                  'card--plain'
                ]"
                header-class="pb-0"
              >
                <template #header>
                  <ui-wad class="d-flex gap-6 align-items-center justify-content-space-between">

                    <ui-blank
                      v-if="memberList.length === 0"
                    />

                    <span
                      v-if="memberList.length > 0"
                    >
                      {{ memberList.length }}
                      {{ memberList.length > 1 ? `membres` : `membre` }}
                    </span>

                    <ui-tooltip-wrapper
                      class="d-inline-block"
                      v-bind="{
                        title: `Vous n'avez pas les droits pour effectuer cette action`,
                        variant: UI.VARIANT.LIGHT,
                      }"
                      :status="hasACL(ACLS.HAMAC_MEMBRE_CREATE) ?
                        UI.STATUS.DISABLED :
                        UI.STATUS.ENABLED
                      "
                    >
                      <ui-button
                        data-cy="add_member"
                        :variant="UI.VARIANT.PRIMARY"
                        :size="UI.SIZE.SMALL"
                        :status="hasACL(ACLS.HAMAC_MEMBRE_CREATE) ?
                          UI.STATUS.ENABLED :
                          UI.STATUS.DISABLED
                        "
                        outline
                        @click="() => showModal(INVITATION_MODAL_NAME)"
                      >
                        <ui-icon
                          name="person_add"
                        />
                        &nbsp;
                        Ajouter
                      </ui-button>
                    </ui-tooltip-wrapper>

                  </ui-wad>
                </template>

                <template #default>

                  <member-list
                    v-if="memberList.length > 0"
                    class="page-team-details__list"
                    :team-name="details.label"
                    :list="memberList"
                    :delete-callback="deleteMemberCallback"
                  />

                  <ui-caveat
                    v-if="memberList.length === 0"
                    class="text-align-center py-4"
                  >
                    <ui-icon
                      name="person_off"
                      class="d-block font-size-30px mb-0.5 color-gray-medium"
                      :theme="UI.ICON_THEME.FILLED"
                    />
                    <span class="d-block font-weight-light color-gray-medium">
                      Aucun utilisateur
                    </span>
                  </ui-caveat>

                </template>
              </b-card>

            </div>
          </ui-wad>
        </template>
      </ui-cloak>

      <!-- MODAL: Member Invitation -->
      <ui-modal
        :id="INVITATION_MODAL_NAME"
        :focus="false"
        :size="UI.SIZE.SMALL"
        no-footer
        teleport-disabled
      >
        <template #title>
          Ajouter des nouveaux utilisateurs
        </template>
        <user-invitation
          :invite-callback="inviteUserCallback"
          :default-data="{
            list: userList_
          }"
          @close="hideModal(INVITATION_MODAL_NAME)"
        />
      </ui-modal>

    </template>

  </ui-route-aware-sidebar-container>
</template>

<style lang="sass" scoped>
@import '~/assets/sass/modules/layout/variables'
@import '~/assets/sass/modules/layout/members'

.page-team-details

  &__preloading
    text-align: center
    margin-top: 30vh

  &__separator
    height: 1px
    background-color: $border-color
    margin-bottom: $layout-spacer-y * .5

</style>

<style lang="sass">
@import '~/assets/sass/modules/layout/members'

// @XXX Vue3 lack of State-Driven Dynamic CSS API
#hamac-team-member-invitation-modal

  .modal-body
    // reset "modal-body" padding due to presence of inner footer
    padding-bottom: initial

    // reset "modal-body" padding due to presence of inner top teaser
    padding-top: initial

</style>

<script lang="ts">
import DetailsForm from '~/components/hamac/team/DetailsFormEdit.vue'
import UserInvitation from '~/components/hamac/team/UserInvitation.vue'
import MemberList from '~/components/hamac/team/MemberList.vue'
import Console from '~/utils/lib/core/console'
import { any, clone, delay, equals, join, noop, prop, reject, sustainedPromise, tapCatch, toString } from '~/utils/lib/core/prelude'
import { getListRoute, type Team } from '~/stores/hamac/team'
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import { useLegacyPageFetch } from '~/composables/core/hook'
import { useToastBuilder } from '~/composables/layout/toast'
import { useLegacyModalController } from '~/composables/layout/modal'
import { useCompanyRequiredNavigationContext } from '~/composables/layout/navigation'
import { querySelector } from '~/utils/lib/core/dom'
import { useAccessControl } from '~/composables/layout/access-control'
import { useStore_hamac_team_details } from '~/stores/hamac/team/details'
import { useStore_hamac_team_member } from '~/stores/hamac/team/member'
import type { Member } from '~/stores/hamac/team/member'
import { useStore_hamac_user_list } from '~/stores/hamac/user/list'
import type { Company } from '~/stores/client/access'
import type { User } from '~/stores/hamac/user'
import type { UiReset } from '#components'
import { EVENTS } from '~/stores/hamac/team/list'

const console = new Console('component:team-details')

const HIRE_TEAM_DETAILS_MODAL_ID = `hire-team-details-modal-id`
const INVITATION_MODAL_NAME = `hamac-team-member-invitation-modal`

export default defineNuxtComponent({
  components: { UserInvitation, DetailsForm, MemberList },
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_REQUIRED,
      menuItem: MENU_ITEM.EMBAUCHE,
      scrollPolicy: 'preserve-when-involved',
    })
    const navigationContext = useCompanyRequiredNavigationContext()

    useHead({
      title: `Équipe`,
    })

    const triggeredParentListRefresh
      = inject(EVENTS.ON_TEAM_LIST_REFRESH_NEEDED, noop)

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const {
      hideModal,
      showModal,
    } = useLegacyModalController()

    const ToastBuilder = useToastBuilder()

    const ref_reset_detailsForm
      = useTemplateRef<InstanceType<typeof UiReset>>('reset_detailsForm')

    const store_team_details = useStore_hamac_team_details()
    const store_team_member = useStore_hamac_team_member()
    const store_user_list = useStore_hamac_user_list()

    return {
      ...navigationContext,
      ...useAccessControl(),
      fetchState,
      fetchWrapper,
      hideModal,
      showModal,
      ToastBuilder,
      ref_reset_detailsForm,
      store_team_details,
      store_team_member,
      store_user_list,
      triggeredParentListRefresh,
    }
  },
  data() {
    const parentRoute = (() => {
      const {
        structure: { id },
        company: { code_societe }
        // @ts-expect-error: "data" hook not injecting "setup" dependencies
      } = this.fromCompanyLevel()

      return getListRoute(id, code_societe)
    })()

    return {
      HIRE_TEAM_DETAILS_MODAL_ID,
      INVITATION_MODAL_NAME,
      parentRoute,
      onDetailsPending: false,
      details: {} as unknown as Team,
      memberList: [] as Array<Member>,
      userList: [] as Array<User>,
      isPending: false,
      // predicates
      userPredicate: (item, item_) => {
        const id  = prop('compte_id', item)
        const id_ = prop('compte_id', item_)
        return equals(id, id_)
      },
    }
  },
  computed: {
    // trimmed of user already added into the team member list
    userList_() {
      return reject(
        item => any(
          item_ => this.userPredicate(item, item_),
          this.memberList
        ),
        this.userList
      )
    }
  },
  created() {
    this.fetchWrapper(
      () => this.fetch()
    )
  },
  methods: {
    /**
     * @desc
     *    - update document title accordingly
     *    - update page title accordingly
     */
    updateCallback(data) {
      const opts = {
        data,
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        uuid_hamac_equipe: this.details.uuid_hamac_equipe
      }

      this.onDetailsPending = true

      return sustainedPromise(
          300,
          this.store_team_details.update(opts),
        )
        .then(details => {
          this.onDetailsPending = false
          this.ToastBuilder
            .success()
            .class('hire-team-edit-toast')
            .title(`Équipe modifiée`)
            .show()

          setPageTitle(
            prop('label', data),
           this.fromCompanyLevel().company.raison_sociale
          )

          this.details = clone( details )

          this.ref_reset_detailsForm?.reset()

          this.triggeredParentListRefresh()
        })
        .catch(err => {
          console.warn('update', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Modification équipe`)
            .show()
        })
        .finally(() => this.isPending = false)
    },
    inviteUserCallback(data) {
      const opts = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        uuid_hamac_equipe: this.details.uuid_hamac_equipe
      }

      return this.store_team_member.invite(
          { ...opts, data }
        )
        // renew member list
        .then(() => this.store_team_member.list(opts))
        .then(data => {
          this.memberList = clone(data)
          this.triggeredParentListRefresh()
        })
        .then(() => this.ToastBuilder
          .success()
          .title(`Ajout effectué !`)
          .show()
        )
        .catch(tapCatch(err => {
          console.warn('inviteMember', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Ajout de nouveaux membres`)
            .show()
        }))
    },
    deleteMemberCallback({ uuid_hamac_equipe_membre }) {
      const opts = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        uuid_hamac_equipe: this.details.uuid_hamac_equipe
      }

      return this.store_team_member.delete(
          { ...opts, uuid_hamac_equipe_membre }
        )
        .then(() => removeRow(uuid_hamac_equipe_membre))
        // renew member list
        .then(() => this.store_team_member.list(opts))
        .then(data => {
          this.memberList = clone(data)
          this.triggeredParentListRefresh()
        })
        .catch(tapCatch(err => {
          console.warn('deleteMember', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Suppression membre`)
            .show()
        }))
    },
    fetch() {
      const uuidData = this.fromCompanyLevel().structure.uuidData
      const code_societe = this.fromCompanyLevel().company.code_societe
      const uuid_hamac_equipe = this.$route.params.teamCode as string

      const userManagementSpecifics = (() => {
        switch (this.hasACL(this.ACLS.HAMAC_EQUIPE_AVAILABLE_USER)) {

          case false:
            return Promise.resolve([])

          case true:
            return this.store_user_list.read(
              { uuidData, code_societe }
            )
        }
      })()

      return Promise.all([
          this.store_team_details.read(
            { uuidData, code_societe, uuid_hamac_equipe }
          ),
          userManagementSpecifics
        ])
        .then(results => {
          const details = results[0]
          const userList = results[1]

          setPageTitle(
            details.label,
          this.fromCompanyLevel().company.raison_sociale
          )

          // Component data
          this.details = clone(details)
          this.memberList = prop('membres', details)
          this.userList = userList
        })
        .catch(tapCatch(err => {
          console.warn('fetch', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Fiche équipe`)
            .show()
        }))
    }
  }
})

function setPageTitle(
  label :Team["label"],
  raison_sociale :Company["raison_sociale"]
) {
  document.title = join(' ', [
    `Équipe « ${label} »`,
    `— ${raison_sociale}`,
  ])
}

async function removeRow(
  uuid_hamac_equipe_membre :Member["uuid_hamac_equipe_membre"]
) {
  // start delete animation
  querySelector(`tr[data-pk="${uuid_hamac_equipe_membre}"]`)
    .map( element => element.classList.add('member-list__delete-animation') )

  // (defensive waiting preventing overload stimulation before next IO)
  await delay(450)
}
</script>
